import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { endOfDay, startOfDay } from 'date-fns'
import { useCallback, useRef, useState } from 'react'
import { type OrderStatus, OrdersDocument, OrderType } from '../gql/graphql'
import { useSiteContext } from '../hooks'

gql`
  query Orders($siteId: UUID!, $filter: OrderFilterInput, $first: Int, $after: String) {
    orders(siteId: $siteId, filter: $filter, first: $first, after: $after) {
      edges {
        node {
          id
          orderType
          status
          amount
          detail
          userName
          userMobile
          userAvatarUrl
          createTime
          payTime
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

const statusLabels: Record<
  OrderStatus,
  { label: string; color: 'success' | 'warning' | 'default' | 'error' }
> = {
  PENDING: { label: '待支付', color: 'warning' },
  CONFIRMED: { label: '已支付', color: 'success' },
  CANCELLED: { label: '已取消', color: 'default' },
  CLOSED: { label: '已关闭', color: 'error' },
}

function formatDetail(detail: unknown): string {
  if (!Array.isArray(detail)) return '-'
  return detail
    .map(
      (d: { court_name?: string; date?: string; hour?: number }) =>
        `${d.court_name ?? ''} ${d.date ?? ''} ${d.hour ?? ''}:00`,
    )
    .sort()
    .join('\n')
}

export default function OrdersPage() {
  const { currentSite } = useSiteContext()
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [mobileInput, setMobileInput] = useState('')
  const [mobile, setMobile] = useState<string | undefined>(undefined)

  const { data, loading, error, fetchMore } = useQuery(OrdersDocument, {
    variables: {
      siteId: currentSite?.id as string,
      filter: {
        startDate: startDate ? startOfDay(startDate).toISOString() : undefined,
        endDate: endDate ? endOfDay(endDate).toISOString() : undefined,
        mobile,
      },
    },
    skip: !currentSite,
    fetchPolicy: 'network-only',
  })

  const loadingMore = useRef(false)

  const isValidMobile = (value: string): boolean => {
    return value === '' || /^1[3-9]\d{9}$/.test(value)
  }

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 11)
    setMobileInput(value)
  }

  const handleSearch = () => {
    if (isValidMobile(mobileInput)) {
      setMobile(mobileInput || undefined)
    }
  }

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
      if (
        scrollHeight - scrollTop - clientHeight < 100 &&
        data?.orders.pageInfo.hasNextPage &&
        !loadingMore.current
      ) {
        loadingMore.current = true
        fetchMore({
          variables: { after: data.orders.pageInfo.endCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev
            return {
              ...fetchMoreResult,
              orders: {
                ...fetchMoreResult.orders,
                edges: [...prev.orders.edges, ...fetchMoreResult.orders.edges],
              },
            }
          },
        }).finally(() => {
          loadingMore.current = false
        })
      }
    },
    [data, fetchMore],
  )

  const orders = data?.orders.edges.map((e) => e.node) ?? []

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 48px)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4">订单管理</Typography>
        <Box sx={{ flex: 1 }} />
        <DatePicker
          label="开始日期"
          value={startDate}
          onChange={setStartDate}
          format="yyyy-MM-dd"
          maxDate={endDate || undefined}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 200 },
              slotProps: { inputLabel: { shrink: true } },
            },
            field: { clearable: true, onClear: () => setStartDate(null) },
          }}
        />
        <DatePicker
          label="结束日期"
          value={endDate}
          onChange={setEndDate}
          format="yyyy-MM-dd"
          minDate={startDate || undefined}
          maxDate={new Date()}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 160 },
              slotProps: { inputLabel: { shrink: true } },
            },
          }}
        />
        <TextField
          label="手机号"
          size="small"
          value={mobileInput}
          onChange={handleMobileChange}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          slotProps={{ inputLabel: { shrink: true } }}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 11,
          }}
          sx={{ width: 160 }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSearch}
          disabled={!isValidMobile(mobileInput)}
        >
          搜索
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">加载订单失败: {error.message}</Alert>}

      {data && (
        <TableContainer
          component={Paper}
          sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
          onScroll={handleScroll}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>类型</TableCell>
                <TableCell>客户</TableCell>
                <TableCell>详情</TableCell>
                <TableCell>下单时间</TableCell>
                <TableCell>状态</TableCell>
                <TableCell align="right">金额</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    暂无订单
                  </TableCell>
                </TableRow>
              )}
              {orders.map((order) => {
                const st = statusLabels[order.status]
                return (
                  <TableRow key={order.id}>
                    <TableCell>
                      {order.orderType === OrderType.SlotBooking
                        ? '订场'
                        : order.orderType}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={order.userAvatarUrl ?? undefined}
                          sx={{ width: 28, height: 28 }}
                        />
                        <div>
                          <div>{order.userName ?? '-'}</div>
                          {order.userMobile && (
                            <div style={{ color: '#888', fontSize: '0.8em' }}>
                              {order.userMobile}
                            </div>
                          )}
                        </div>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-line' }}>
                      {formatDetail(order.detail)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(order.createTime).toLocaleString('zh-CN')}
                    </TableCell>
                    <TableCell>
                      <Chip label={st.label} color={st.color} size="small" />
                    </TableCell>
                    <TableCell align="right">¥{order.amount}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
